import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { UK_SIZES, imgUrl } from "../lib/shop";
import { UploadButton } from "./Images";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export function ColourEditor({ colours, onAdd, onRemove }) {
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#ffffff");
  const add = () => {
    const n = name.trim();
    if (!n || colours.some((c) => c.name.toLowerCase() === n.toLowerCase())) return;
    onAdd({ name: n, hex });
    setName("");
  };
  return (
    <div data-testid="colour-editor">
      <div className="flex flex-wrap gap-2 mb-3">
        {colours.map((c) => (
          <span key={c.name} className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-full border border-white/15 text-sm" data-testid={`colour-chip-${slug(c.name)}`}>
            <i className="w-4 h-4 rounded-full border border-white/30" style={{ background: c.hex }} /> {c.name}
            <button type="button" onClick={() => onRemove(c.name)} className="p-1 text-red-400" aria-label={`Remove ${c.name}`} data-testid={`colour-remove-${slug(c.name)}`}><X size={14} /></button>
          </span>
        ))}
        {!colours.length && <p className="text-xs text-zinc-500">No colours yet. Add one to let customers choose a colour.</p>}
      </div>
      <div className="flex gap-2">
        <input type="color" value={hex} onChange={(e) => setHex(e.target.value)} className="w-12 h-12 rounded-lg bg-transparent border border-white/15 cursor-pointer" aria-label="Colour swatch" data-testid="colour-hex-input" />
        <input className="field" placeholder="Colour name, e.g. White" value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="colour-name-input" />
        <button type="button" className="chip flex items-center gap-1 shrink-0" onClick={add} data-testid="colour-add-button"><Plus size={14} /> Add</button>
      </div>
    </div>
  );
}

export function ColourVariants({ variants, colours, onChange, basePrice, images = [], onAddImages }) {
  const names = colours.map((c) => c.name);
  const [active, setActive] = useState(names[0] || "");
  const [size, setSize] = useState("");
  useEffect(() => { if (!names.includes(active)) setActive(names[0] || ""); }, [names, active]);
  const key = names.length ? active : "";
  const rows = variants.map((v, i) => ({ v, i })).filter(({ v }) => (v.colour || "") === key);
  const has = (s) => rows.some(({ v }) => v.size === s);
  const addSizes = (list) => onChange([...variants, ...list.filter((s) => s && !has(s)).map((s) => ({ size: s, stock: 0, colour: key }))]);
  const upd = (i, patch) => onChange(variants.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const firstSizes = variants.filter((v) => v.colour === names[0]).map((v) => v.size);
  const photoCount = (n) => images.filter((im) => im.colour === n).length;
  const num = (s) => (s === "" ? null : Number(s));
  return (
    <div data-testid="variant-editor">
      {names.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {colours.map((c) => (
            <button type="button" key={c.name} onClick={() => setActive(c.name)} data-testid={`variant-colour-tab-${slug(c.name)}`}
              className={`chip flex items-center gap-2 ${active === c.name ? "chip-active" : ""}`}>
              <i className="w-3.5 h-3.5 rounded-full border border-black/30" style={{ background: c.hex }} /> {c.name}
              <span className="text-[10px] opacity-70">{photoCount(c.name)} pics · {variants.filter((v) => v.colour === c.name).reduce((s, v) => s + v.stock, 0)} prs</span>
            </button>
          ))}
        </div>
      )}
      {key && onAddImages && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-xl bg-white/5">
          {images.filter((im) => im.colour === key).slice(0, 4).map((im) => <img key={im.url} src={imgUrl(im.url)} alt="" className="w-12 h-12 rounded-lg object-cover" />)}
          <UploadButton label={`Upload ${key} photos`} testid="variant-colour-upload" onUploaded={(added) => onAddImages(added.map((a) => ({ ...a, colour: key })))} />
        </div>
      )}
      {rows.length > 0 && (
        <div className="grid grid-cols-[56px_1fr_1fr_1fr_36px] gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
          <span>Size</span><span>Stock</span><span>Price (R)</span><span>SKU</span><span />
        </div>
      )}
      <div className="space-y-2">
        {rows.map(({ v, i }) => (
          <div key={`${key}-${v.size}`} className="grid grid-cols-[56px_1fr_1fr_1fr_36px] gap-1.5 items-center" data-testid={`variant-row-${slug(v.size)}`}>
            <span className="font-bold text-xs">{v.size}{!v.stock && <span className="block text-[9px] text-red-400">SOLD OUT</span>}</span>
            <input type="number" min="0" className="field !px-2 !min-h-[40px]" value={v.stock} aria-label={`Stock for ${v.size}`} data-testid={`variant-stock-${slug(v.size)}`}
              onChange={(e) => upd(i, { stock: Math.max(0, parseInt(e.target.value || "0", 10)) })} />
            <input type="number" min="0" step="0.01" className="field !px-2 !min-h-[40px]" value={v.price ?? ""} placeholder={basePrice ? String(basePrice) : "Default"} aria-label={`Price for ${v.size}`} data-testid={`variant-price-${slug(v.size)}`}
              onChange={(e) => upd(i, { price: num(e.target.value) })} />
            <input className="field !px-2 !min-h-[40px]" value={v.sku || ""} placeholder="SKU" aria-label={`SKU for ${v.size}`} data-testid={`variant-sku-${slug(v.size)}`}
              onChange={(e) => upd(i, { sku: e.target.value || null })} />
            <button type="button" onClick={() => onChange(variants.filter((_, j) => j !== i))} className="p-2 text-red-400" aria-label="Remove size"><Trash2 size={16} /></button>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-zinc-500">No sizes{key ? ` for ${key}` : ""} yet.</p>}
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">Leave price empty to use the product price. 0 stock = SOLD OUT for that colour & size.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        {key && key !== names[0] && firstSizes.length > 0 && <button type="button" className="chip" onClick={() => addSizes(firstSizes)} data-testid="variant-copy-sizes">Copy sizes from {names[0]}</button>}
        <input className="field !w-32" placeholder="e.g. UK 12" value={size} onChange={(e) => setSize(e.target.value)} data-testid="variant-new-size-input" />
        <button type="button" className="chip" onClick={() => { addSizes([size.trim()]); setSize(""); }} data-testid="variant-add-button">Add size</button>
        <button type="button" className="chip" onClick={() => addSizes(UK_SIZES)} data-testid="variant-add-uk-range">Add UK 3–11</button>
      </div>
    </div>
  );
}
